import memoize from 'lru-memoize';
import React, {FC, useState} from 'react';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {ChannelModel, RChannelEmail} from '../../../../../libs/shared/core/src/models/api/channelModel';
import {getHasOmniChannelLinkedConversations} from '../../../../../libs/shared/core/src/store/featureFlags/featureFlagsSelectors';
import {DropdownProps} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {SearchDropdown} from '../../../../../libs/shared/ui/src/components/dropdowns/search/searchDropdown';
import {useAppSelector} from '../../../../helpers/appReduxHelpers';
import {ChannelDropdownItem} from './channelDropdownItem';

/*
 * Props.
 */

interface ChannelPickerDropdownProps extends DropdownProps {
  channel: ChannelModel;
  alternativeChannels: ReadonlyArray<ChannelModel>;
  onChannelSelected: (model: ChannelModel) => void;
  isConversationLinked: boolean;
}

/*
 * Component.
 */

export const PickChannelDropdown: FC<ChannelPickerDropdownProps> = (props) => {
  const {alternativeChannels, isConversationLinked} = props;
  const [query, setQuery] = useState('');
  const hasOmniChannelLinkedConversations = useAppSelector(getHasOmniChannelLinkedConversations);

  const filteredData = filterRows(
    alternativeChannels,
    query,
    !hasOmniChannelLinkedConversations && isConversationLinked,
  );
  const renderRow = (channel: ChannelModel, index: number) => (
    <ChannelDropdownItem
      key={channel.id}
      index={index}
      currentChannel={props.channel}
      channel={channel}
      onSelect={() => props.onChannelSelected(channel)}
    />
  );

  return (
    <SearchDropdown
      {...props}
      data={filteredData}
      query={query}
      onQueryChange={setQuery}
      renderRow={renderRow}
      width={260}
      headerTitle={<Trans id="composer-channel-picker-title">Channels</Trans>}
    />
  );
};

const filterRows = memoize(20)(
  (channels: ReadonlyArray<ChannelModel>, query: string, isEmailOnly?: boolean) => {
    // TODO: Remove when removing omnichannel ff
    const maybeFilteredNonEmailChannels = isEmailOnly
      ? channels.filter((c) => RChannelEmail.is(c))
      : channels;
    // Shortcut for empty query.
    if (!query) {
      return maybeFilteredNonEmailChannels;
    }

    // Otherwise, filter by name.
    const lowerCaseQuery = query.toLowerCase();
    return maybeFilteredNonEmailChannels.filter((c) => c.displayName.toLowerCase().includes(lowerCaseQuery));
  },
);
